import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bell, ChevronDown, ChevronUp, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/useSeo";

function formatWeek(iso) {
  try {
    const d = new Date(iso);
    const end = new Date(d);
    end.setDate(end.getDate() + 6);
    const fmt = (x) => x.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
    return `${fmt(d)} → ${fmt(end)}`;
  } catch {
    return iso;
  }
}

export default function DigestHistory() {
  useSeo({
    title: "Historique des digests — Student Hub",
    description: "Retrouve tous tes résumés hebdomadaires personnalisés Student Hub.",
    canonical: "https://studenthubfrance.fr/notifications/history",
    noindex: true,
  });

  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [h, p] = await Promise.all([
          api.get("/digests/mine?limit=30"),
          api.get("/digests/preview"),
        ]);
        setItems(h.data || []);
        setPreview(p.data.digest);
        setMode(p.data.mode || "all");
      } catch { /* silent */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="digest-history-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">
          Notifications · Historique
        </p>
        <h1 className="font-display text-4xl font-bold text-slate-900 mt-2 flex items-center gap-3">
          <Bell size={28} className="text-indigo-500" />
          Historique des digests
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Retrouve tous tes résumés hebdomadaires. Chaque dimanche matin, Student Hub te prépare une
          revue personnalisée de la semaine, en fonction de tes préférences de notifications.
        </p>
      </header>

      {/* Preview of THIS week (before Sunday) */}
      {preview && (
        <section
          className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-fuchsia-50 p-5 sm:p-6"
          data-testid="digest-preview-card"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500 text-white flex items-center justify-center shrink-0">
              <Sparkles size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-indigo-600 font-semibold">
                Aperçu · en cours de préparation
              </p>
              <p className="font-display font-bold text-lg text-slate-900 mt-1">{preview.title}</p>
              <ul className="mt-3 space-y-1.5 text-sm text-slate-700" data-testid="digest-preview-bullets">
                {preview.bullets.map((b, i) => (
                  <li key={i} className="flex items-baseline gap-2">
                    <span className="text-indigo-500">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mt-3">
                Ton digest sera envoyé <strong>dimanche matin à 9h</strong> (heure de Paris).{" "}
                <Link to="/settings" className="text-indigo-600 hover:text-indigo-700 font-medium">
                  Changer mes préférences →
                </Link>
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Mode banner */}
      <div className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm text-slate-700 flex items-center justify-between gap-3">
        <span className="flex items-center gap-2">
          <span className={`inline-block w-2 h-2 rounded-full ${mode === "off" ? "bg-slate-400" : "bg-emerald-500"}`} />
          Mode digest : <strong className="font-medium">{mode === "all" ? "Tout" : mode === "important_only" ? "Important uniquement" : "Désactivé"}</strong>
        </span>
        <Link to="/settings" className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">
          Modifier <ArrowRight size={12} />
        </Link>
      </div>

      {/* History list */}
      {loading && (
        <p className="text-sm text-slate-400 flex items-center gap-1">
          <Loader2 size={14} className="animate-spin" /> Chargement…
        </p>
      )}
      {!loading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <Bell size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">Aucun digest reçu pour l'instant</p>
          <p className="text-xs text-slate-400 mt-1">
            Le premier arrive dimanche prochain à 9h.
          </p>
        </div>
      )}
      {!loading && items.length > 0 && (
        <ul className="space-y-3" data-testid="digest-history-list">
          {items.map((d) => {
            const isOpen = !!expanded[d.id];
            return (
              <li
                key={d.id}
                className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
                data-testid={`digest-item-${d.id}`}
              >
                <button
                  onClick={() => setExpanded({ ...expanded, [d.id]: !isOpen })}
                  className="w-full flex items-center justify-between gap-3 p-5 hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-slate-500 font-mono">
                      Semaine du {formatWeek(d.week_start)}
                    </p>
                    <p className="font-display font-semibold text-slate-900 mt-0.5">{d.title}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {d.total_items} élément{d.total_items > 1 ? "s" : ""} · {d.categories?.length || 0} catégorie{(d.categories?.length || 0) > 1 ? "s" : ""}
                    </p>
                  </div>
                  {isOpen ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                </button>
                {isOpen && (
                  <div className="border-t border-slate-100 p-5 bg-slate-50">
                    <ul className="space-y-1.5 text-sm text-slate-700">
                      {d.bullets.map((b, i) => (
                        <li key={i} className="flex items-baseline gap-2">
                          <span className="text-indigo-500">•</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
